import * as React from 'react';
import { Component } from 'react';
import { toast } from 'react-toastify';
import ErrorMessage from './ErrorMessage';
import ApiException from '../lib/ApiException';
import InvalidOrExpired from '../appComponents/pages/InvalidOrExpired';
import { hasValue, isObject, nestedArrayReducer } from '../lib/utils';

/**
 * @deprecated replace with hooks & useAsyncAction if required
 */
export default class AsyncActionContainer<P, S extends AsyncActionContainerState> extends Component<P, S> {
	abortController: AbortController = null;

	constructor(props: any) {
		super(props);
		// eslint-disable-next-line react/no-unused-state
		this.state = { loading: false, errorMessage: null } as S;
	}

	componentWillUnmount() {
		if (this.abortController !== null) {
			this.abortController.abort();
			this.abortController = null;
		}
	}

	performAction = async (action: () => Promise<void>, showToastForError?: boolean) => {
		let errorMessage: string = null;
		// eslint-disable-next-line react/no-unused-state
		this.setState({ errorMessage, loading: true });

		try {
			await action();
		} catch (e) {
			errorMessage = prepareErrorMessage(e);
			this.setState({ errorMessage });
		} finally {
			// eslint-disable-next-line react/no-unused-state
			this.setState({ loading: false });
			if (errorMessage && showToastForError) toast(errorMessage, { bodyClassName: 'error' });
			// eslint-disable-next-line no-unsafe-finally
			return errorMessage;
		}
	};

	renderError() {
		if (this.state.errorMessage.includes('Unauthorized')) {
			return <InvalidOrExpired />;
		}
		if (this.state.errorMessage.includes('html')) {
			return <ErrorMessage message="We are currently experiencing high load, please retry your action later" />;
		}
		return <ErrorMessage message={`Unexpected Error: ${this.state.errorMessage}`} />;
	}

	render() {
		return this.renderError();
	}
}

export interface AsyncActionContainerState {
	loading: boolean;
	errorMessage?: string;
}

// TODO: Move all generic error code to a different file.
export const prepareErrorMessage = (error: ApiException | CoreApiError | { message: string }) => {
	let errorMessage: string;
	if (isApiException(error)) {
		if (error.body !== null && error.body !== '') {
			errorMessage = error.body;
		} else {
			errorMessage = error.statusText || getStatusCodeMessage(error.status);
		}
	} else if (isCoreApiError(error)) {
		const coreError = error as CoreApiError;
		if (hasValue(coreError.body.errors)) {
			const errors = Object.values(coreError.body.errors).reduce(nestedArrayReducer);
			errorMessage = errors.join(' ');
		} else {
			errorMessage = (coreError.body as any).detail || (coreError.body as any).title || '';
		}
	} else {
		errorMessage = error.message;
	}
	return getCustomisedErrorMessage(errorMessage);
};

const getStatusCodeMessage = (status: number): string => {
	switch (status) {
		case 400:
			return 'The request was invalid. Please check the form and try again.';
		case 401:
			return 'You are not authorised to perform this action. Please log in again.';
		case 403:
			return 'You do not have permission to perform this action.';
		case 404:
			return 'The requested resource was not found.';
		case 500:
			return 'An unexpected server error occurred. Please try again later.';
		default:
			return `An error occurred (${status}).`;
	}
};

const getCustomisedErrorMessage = (errorMessage: string) => {
	if (!hasValue(errorMessage)) {
		return 'An error occurred but there is no error message.';
	}
	return errorMessage;
};

const isApiException = (exception: any): exception is ApiException =>
	'body' in exception && typeof exception.body === 'string';

const isCoreApiError = (exception: any): exception is CoreApiError =>
	hasValue(exception) && 'body' in exception && isObject(exception.body);

interface CoreApiError {
	body: CoreApiErrorBody;
}

interface CoreApiErrorBody {
	type: string;
	title: string;
	status: number;
	traceId: string;
	errors: Record<string, string[]>;
}
